// Browser-side TVStor client: same-origin baseUrl (the /api/v1 rewrite proxies to the
// backend) with the bearer token read from the session cookie at call time.

import { TVStorApi } from 'trantor-tvstor-api-js'

import { getSession } from '@/utils/session'
import { expiryVerdictOf, notifySessionExpired, SESSION_PROBE_PATH } from '@/libs/sessionExpiry'

function pathOf(input: RequestInfo | URL): string {
  return input instanceof Request
    ? new URL(input.url).pathname
    : new URL(String(input), window.location.origin).pathname
}

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
  return input instanceof Request ? input.method : (init?.method ?? 'GET')
}

function authorizationOf(input: RequestInfo | URL, init?: RequestInit): string | null {
  return new Headers(input instanceof Request ? input.headers : (init?.headers ?? {})).get('authorization')
}

// One probe at a time: a page that fires ten parallel reads into a dead session
// must ask the authority ONCE, not ten times.
let probeInFlight: Promise<boolean> | null = null

function sessionIsGone(token: string): Promise<boolean> {
  probeInFlight ??= fetch(SESSION_PROBE_PATH, { headers: { authorization: `Bearer ${token}` } })
    .then(probe => probe.status === 401)

    // The probe did not travel (backend restarting, network blip). NOT proof the
    // session died — and destroying the operator's work on a non-answer is
    // exactly the invented certainty this round removes.
    .catch(() => false)
    .finally(() => {
      probeInFlight = null
    })

  return probeInFlight
}

// The global 401 interceptor (round #101): EVERY response of the browser client
// passes here — an expired or revoked session surfaces as 401 on any call, so the
// clearSession + /login handling lives in ONE place instead of every view's catch.
// The error still propagates to the caller (views keep their non-401 handling);
// navigation is delegated to SessionExpiredRedirect via the session-expiry event.
//
// #87 (2026-08-09): il VERDETTO sta in libs/sessionExpiry (funzione pura,
// testata) — qui resta solo il pezzo che tocca la rete, cioe' interrogare
// l'autorita' quando il 401 potrebbe davvero riguardare la sessione.
export const interceptingFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init)
  const session = getSession()

  const verdict = expiryVerdictOf({
    status: response.status,
    pathname: pathOf(input),
    method: methodOf(input, init),
    authorization: authorizationOf(input, init),
    sessionToken: session ? session.token : null,
    currentPath: window.location.pathname
  })

  if (verdict === 'expired') {
    notifySessionExpired()
  } else if (verdict === 'probe' && session && (await sessionIsGone(session.token))) {
    notifySessionExpired()
  }

  return response
}

export function apiClient() {
  const session = getSession()

  return TVStorApi.createClientV1({
    baseUrl: '',
    fetch: interceptingFetch,
    ...(session ? { token: session.token } : {})
  })
}

// A client bound to an explicit token — for the pre-cookie window of the login flow
// (the forced mustChangePassword change happens BEFORE the session cookie exists).
// No interceptor: there is no session to expire, and the flow lives on /login.
export function apiClientWithToken(token: string) {
  return TVStorApi.createClientV1({ baseUrl: '', token })
}
